import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { LucideIcon } from '../ui/LucideIcon';
import type { ModuleRoute } from '../../routes/modules';
import { CompteurOnglet } from './CompteurOnglet';

export interface OngletModule {
  readonly to: string;
  readonly icon: string;
  readonly label: string;
  readonly isActive: boolean;
  /** Compteur optionnel (cf. `ModuleRoute.badge`). */
  readonly badge?: ModuleRoute['badge'];
}

/**
 * Rangée d'onglets modules — défilement horizontal DANS son conteneur.
 *
 * Avant : la rangée était simplement tronquée au bord droit. À 1440 px, avec 12
 * modules, « Vérité des factures » n'existait visuellement pas — pas de fondu,
 * pas de flèche, aucun indice. Un utilisateur ne pouvait pas savoir qu'il lui
 * manquait des écrans.
 *
 * Règle maison : on ne masque jamais une fonction derrière un menu « … ». Tout
 * reste sur la même ligne, atteignable au doigt ou à la flèche, avec un fondu
 * qui dit franchement « ça continue par là ». L'onglet actif est toujours
 * ramené dans le champ de vision — jamais en scrollant la page, uniquement le
 * conteneur.
 */
/** Largeur du fondu de bord — doit rester synchro avec le `w-16` de `BordDefilement`. */
const LARGEUR_FONDU_PX = 64;
/**
 * Marge de sécurité pour « ramener l'onglet actif en vue ». Elle doit dépasser
 * le fondu : à 24 px, l'onglet actif était bien dans le conteneur mais sa fin
 * passait sous le dégradé blanc et semblait tronquée — vu à la capture du
 * 10/08 sur « Vérité des factures ». Au-delà du contenu, le navigateur borne
 * `scrollLeft`, le fondu de ce côté disparaît, et l'onglet est net.
 */
const MARGE_VUE_PX = LARGEUR_FONDU_PX + 8;
const TOLERANCE_PX = 2;

export function OngletsModules({ onglets }: { readonly onglets: ReadonlyArray<OngletModule> }): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contenuRef = useRef<HTMLDivElement>(null);
  const [debordeGauche, setDebordeGauche] = useState(false);
  const [debordeDroite, setDebordeDroite] = useState(false);

  const majDebordement = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    setDebordeGauche(sc.scrollLeft > TOLERANCE_PX);
    setDebordeDroite(sc.scrollLeft + sc.clientWidth < sc.scrollWidth - TOLERANCE_PX);
  }, []);

  /** Ramène l'onglet actif dans le champ de vision — en bougeant le conteneur, jamais la page. */
  const assurerVisibiliteActif = useCallback(() => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const actif = sc.querySelector<HTMLElement>('[aria-current="page"], [data-actif="true"]');
    if (!actif) return;
    const zone = sc.getBoundingClientRect();
    const cible = actif.getBoundingClientRect();
    if (cible.left < zone.left + MARGE_VUE_PX) {
      sc.scrollLeft = Math.max(0, sc.scrollLeft - (zone.left + MARGE_VUE_PX - cible.left));
    } else if (cible.right > zone.right - MARGE_VUE_PX) {
      sc.scrollLeft += cible.right - (zone.right - MARGE_VUE_PX);
    }
    majDebordement();
  }, [majDebordement]);

  /**
   * On observe le CONTENU, pas seulement le conteneur : la boîte du conteneur
   * fait toujours la largeur de la fenêtre et ne bouge donc jamais, alors que
   * la bande d'onglets, elle, s'élargit après coup — Inter arrive de Google
   * Fonts en `display=swap` (cf. index.html) et la rangée passait de ~1469 à
   * 1582 px APRÈS le premier calcul. Résultat mesuré le 10/08 : l'onglet actif
   * « Vérité des factures » finissait hors champ et la flèche de droite ne
   * s'affichait pas, alors même qu'il restait 61 px à parcourir.
   */
  useEffect(() => {
    majDebordement();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const sc = scrollerRef.current;
    if (!sc) return undefined;
    const ro = new ResizeObserver(() => assurerVisibiliteActif());
    ro.observe(sc);
    if (contenuRef.current) ro.observe(contenuRef.current);
    return () => ro.disconnect();
  }, [assurerVisibiliteActif, majDebordement, onglets.length]);

  /**
   * Filet déterministe pour le même problème : on ne se repose pas sur la seule
   * date de livraison du ResizeObserver (mesurée à ~2 s au chargement en dev,
   * le temps que la page se calme), on repasse dès que les polices sont prêtes.
   */
  useEffect(() => {
    const polices = typeof document === 'undefined' ? undefined : document.fonts;
    if (!polices?.ready) return undefined;
    let annule = false;
    void polices.ready.then(() => {
      if (!annule) assurerVisibiliteActif();
    });
    return () => {
      annule = true;
    };
  }, [assurerVisibiliteActif]);

  // On se cale sur le pathname (et pas sur la prop `isActive`) pour couvrir
  // aussi les modules à sous-routes, où c'est NavLink qui tranche.
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    assurerVisibiliteActif();
  }, [pathname, assurerVisibiliteActif]);

  const defiler = (sens: -1 | 1): void => {
    const sc = scrollerRef.current;
    if (!sc) return;
    const pas = Math.max(160, Math.round(sc.clientWidth * 0.7)) * sens;
    // prefers-reduced-motion : le fondu n'anime déjà rien (apparition binaire),
    // seul ce défilement au clic sur les chevrons est animé — on le rend
    // instantané plutôt que « smooth » quand l'utilisateur l'a demandé.
    const mouvementReduit =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof sc.scrollBy === 'function') sc.scrollBy({ left: pas, behavior: mouvementReduit ? 'auto' : 'smooth' });
    else sc.scrollLeft += pas;
  };

  return (
    <div className="relative">
      <nav
        ref={scrollerRef}
        data-testid="onglets-scroller"
        onScroll={majDebordement}
        className="overflow-x-auto no-scrollbar"
        aria-label="Modules"
      >
        <div ref={contenuRef} className="flex items-center px-5 w-max">
          {onglets.map((o) => (
            <Onglet key={o.to} {...o} />
          ))}
        </div>
      </nav>

      {debordeGauche && <BordDefilement sens={-1} onDefiler={() => defiler(-1)} />}
      {debordeDroite && <BordDefilement sens={1} onDefiler={() => defiler(1)} />}
    </div>
  );
}

/** Fondu (indice de débordement, toutes tailles) + flèche cliquable (souris). */
function BordDefilement({ sens, onDefiler }: { readonly sens: -1 | 1; readonly onDefiler: () => void }): JSX.Element {
  const gauche = sens === -1;
  return (
    <div
      data-testid={gauche ? 'onglets-bord-gauche' : 'onglets-bord-droite'}
      className={[
        'pointer-events-none absolute inset-y-0 flex items-center w-16',
        gauche ? 'left-0 justify-start bg-gradient-to-r' : 'right-0 justify-end bg-gradient-to-l',
        'from-white via-white/95 to-transparent',
      ].join(' ')}
    >
      <button
        type="button"
        onClick={onDefiler}
        tabIndex={-1}
        aria-label={gauche ? 'Faire défiler les onglets vers la gauche' : 'Faire défiler les onglets vers la droite'}
        className={[
          'pointer-events-auto hidden sm:grid place-items-center h-7 w-7 rounded-md',
          'border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 hover:text-slate-900',
          gauche ? 'ml-1.5' : 'mr-1.5',
        ].join(' ')}
      >
        {gauche ? <ChevronLeft size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}

function Onglet({ to, icon, label, isActive, badge }: OngletModule): JSX.Element {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      data-actif={isActive ? 'true' : 'false'}
      className={({ isActive: linkActive }) => {
        const actif = isActive || linkActive;
        return [
          'group inline-flex items-center gap-1.5 h-[42px] px-3 text-sm whitespace-nowrap',
          'border-b-[3px] -mb-px rounded-t-md transition-colors',
          actif
            ? 'text-slate-900 font-semibold border-[var(--accent)] bg-[var(--accent-soft)]/40'
            : 'text-slate-500 font-medium border-transparent hover:text-slate-900 hover:bg-slate-50',
        ].join(' ');
      }}
    >
      {({ isActive: linkActive }) => {
        const actif = isActive || linkActive;
        return (
          <>
            <LucideIcon
              name={icon}
              size={16}
              aria-hidden="true"
              className={actif ? 'text-[var(--accent)]' : 'text-slate-400 group-hover:text-slate-500'}
            />
            <span>{label}</span>
            {badge ? <CompteurOnglet badge={badge} /> : null}
          </>
        );
      }}
    </NavLink>
  );
}
